/**
 * "Forecast – Nächste N Monate".
 *
 * CONTRACTUAL EXPECTATION ONLY.
 * `forecast[]` is the same canonical calculator applied to the months after the
 * selected one: obligations already on record, projected forward. There is no
 * model, no probability and no collection assumption anywhere in it.
 *
 * The design reference's "Forecast bezahlt" and "Forecast offen" columns are
 * absent, and not because they were overlooked — the contract states it in a
 * field: `scope.includes_paid_or_open_forecast === false`. Predicting what will
 * be paid needs a collection model this repository does not have, and filling
 * those columns with anything at all would be the dashboard inventing its own
 * financial facts.
 *
 * "Übrige wiederkehrende" is absent too. The distribution block gets that split
 * from the BACKEND (`expected_recurring_other`); `forecast[]` does not carry it,
 * and computing `recurring_total − recurring_rent` here would be this component
 * deriving a money figure. Arithmetically trivial, categorically wrong place.
 */
import React from "react";

import { fmtMoney, periodLabel } from "./revenueAnalyticsFormat";

/**
 * The permitted columns, as data.
 *
 * Every `field` is a key of `ExpectedMonthPoint`. Keeping the list here rather
 * than as inline JSX means a structural test can assert that no fifth column
 * ever appears without touching the rendering.
 */
export const FORECAST_COLUMNS = [
  { id: "month", label: "Monat", field: null },
  { id: "expected_recurring_rent", label: "Erwartete Miete", field: "expected_recurring_rent" },
  { id: "expected_one_time", label: "Einmalige Gebühren", field: "expected_one_time" },
  { id: "expected_total", label: "Erwartet Total", field: "expected_total" },
];

export default function RevenueForecastCard({ forecast }) {
  const rows = Array.isArray(forecast) ? forecast : [];

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">
        {rows.length > 0
          ? `Forecast – Nächste ${rows.length} ${rows.length === 1 ? "Monat" : "Monate"}`
          : "Forecast"}
      </h2>

      {rows.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Für die kommenden Monate sind keine vertraglichen Erwartungen hinterlegt.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {FORECAST_COLUMNS.map((c, i) => (
                  <th
                    key={c.id}
                    scope="col"
                    className={`py-2 text-xs font-medium text-muted-foreground ${
                      i === 0 ? "" : "text-right"
                    }`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.year}-${r.month}`} className="border-b border-border/60 last:border-b-0">
                  <th scope="row" className="py-2 text-left font-normal text-foreground">
                    {periodLabel(r)}
                  </th>
                  {FORECAST_COLUMNS.slice(1).map((c) => (
                    <td key={c.id} className="py-2 text-right text-foreground">
                      {fmtMoney(r?.[c.field])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Rein vertragliche Erwartung. Keine Zahlungs- oder Ausfallprognose.
      </p>
    </div>
  );
}
